// App.jsx
import { useState, useEffect, lazy, Suspense } from "react";
import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AnimatePresence } from "framer-motion";
import SplashScreen from "./Components/SplashScreen.jsx";
import WelcomeSheet from "./Components/WelcomeSheet.jsx";
import HeaderUnifiedLayout from "./Layouts/UnifiedLayout.jsx";
import ErrorBoundary from "./Components/ErrorBoundary";
import { LocationProvider } from "./context/LocationContext";

// Helper: lazy load con detección de chunks faltantes (nuevo deploy)
const lazyWithRetry = (importFn) => {
  return lazy(() =>
    importFn().catch((error) => {
      const isChunkError = error.message?.includes('Failed to fetch dynamically imported module') ||
                           error.message?.includes('Loading chunk') ||
                           error.message?.includes('Loading CSS chunk') ||
                           error.name === 'ChunkLoadError';

      if (isChunkError) {
        const hasReloaded = sessionStorage.getItem('chunk_reload');
        if (!hasReloaded) {
          sessionStorage.setItem('chunk_reload', 'true');
          window.location.reload();
          return;
        }
        sessionStorage.removeItem('chunk_reload');
      }
      throw error;
    })
  );
};

// Lazy load de todas las páginas
const Checkout = lazyWithRetry(() => import("./Pages/Checkout.jsx"));
const Profile = lazyWithRetry(() => import("./Pages/Profile.jsx"));
const Cart = lazyWithRetry(() => import("./Pages/Cart.jsx"));
const DeliveryTracking = lazyWithRetry(() => import("./Pages/DeliveryTracking.jsx"));
const NewAddress = lazyWithRetry(() => import("./Pages/NewAddress.jsx"));
const AddUserInfo = lazyWithRetry(() => import("./Pages/AddUserInfo.jsx"));
const Favorites = lazyWithRetry(() => import("./Pages/Favorites.jsx"));
// Home = shell del marketplace (se adaptará a single-restaurant: productos del
// restaurante en vez de tarjetas de negocio). Keep-alive food/grocery.
import MarketLayout from "./Layouts/MarketLayout.jsx";

// Spinner de carga para las rutas
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-primary" />
  </div>
);

// Wrapper para envolver cada ruta lazy en Suspense
const Lazy = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

const router = createBrowserRouter([
  {
    path: "/",
    element: <HeaderUnifiedLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/home" replace />,
      },
      {
        path: "home",
        element: <MarketLayout />,
      },
      {
        path: "grocery",
        element: <MarketLayout />,
      },
      {
        path: "cart",
        element: <Lazy><Cart /></Lazy>,
      },
      {
        path: "checkout",
        element: <Lazy><Checkout /></Lazy>,
      },
      {
        path: "delivery-tracking",
        element: <Lazy><DeliveryTracking /></Lazy>,
      },
      {
        path: "new-address",
        element: <Lazy><NewAddress /></Lazy>,
      },
      {
        path: "new-user-info",
        element: <Lazy><AddUserInfo /></Lazy>,
      },
      {
        path: "favorites",
        element: <Lazy><Favorites /></Lazy>,
      },
      {
        path: "profile",
        element: <Lazy><Profile /></Lazy>,
      },
      {
        path: "*",
        element: <Navigate to="/home" replace />,
      },
    ],
  },
]);

export default function App() {
  const [showSplash, setShowSplash] = useState(true);

  // Limpiar flag de reload cuando la app carga correctamente
  useEffect(() => {
    sessionStorage.removeItem('chunk_reload');
  }, []);

  return (
    <>
      {/* Splash encima de la app, como telón: la app ya se está montando y
          cargando detrás, y el splash se va cuando el home avisa que está
          listo (utils/arranque.js). Antes la app se montaba hasta que el
          splash terminaba, y al quitarse se veía la pantalla a medio armar. */}
      <AnimatePresence mode="wait">
        {showSplash && (
          <SplashScreen onFinish={() => setShowSplash(false)} />
        )}
      </AnimatePresence>

      <LocationProvider>
          <ErrorBoundary>
            <RouterProvider router={router} />
          </ErrorBoundary>
          {/* Va por encima de todo (z-[200]): esperar a que se vaya el splash
              para no taparlo. */}
          {!showSplash && <WelcomeSheet />}
          <Toaster
            position="top-center"
            reverseOrder={true}
            gutter={8}
            toastOptions={{
              duration: 2000,
              style: {
                background: '#333',
                color: '#fff',
                borderRadius: '12px',
                padding: '12px 16px',
              },
              success: {
                iconTheme: {
                  primary: '#22c55e',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
      </LocationProvider>
    </>
  );
}
